import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import slug from 'slug';
import { PhoneInputComponent } from "../../../../@shared/components/phone-input/phone-input.component";
import { AuthenticationService } from '../../../../@core/services/authentication.service';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { IconComponent } from '../../../../@core/components/ui/icon/icon.component';
import { BILLING_CYCLE, IOrganization, IUser } from '../../../../@core/models/model';
import { NO_OF_EMPLOYEES, WORKSPACE_PREFIX } from '../../../../common/utils';
import { OrganizationSetupFormService } from '../../../services/organization-setup-from.service';
import { ButtonModule } from 'primeng/button';
import { ToastService } from '../../../../@core/services/toast.service';
import { Router } from '@angular/router';
import { ConsumptionService } from '../../../services/consumption.service';
import { GAUGE_COLORS, setGuageChart } from '../../../utils';
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';
import { environment } from '../../../../../environments/environment.dev';
import { ADDITIONAL_ROLES, USER_ROLE } from '../../../../@core/models/enums';
import { ButtonComponent } from "../../../../@core/components/ui/button/button.component";
import { DataTableComponent } from '../../../../@shared/components/data-table/data-table.component';
import { TableColumn, TableConfig } from '../../../../@shared/models/table.models';
import { SelectComponent, SelectOption, SelectConfig } from "../../../../@core/components/ui/select";
import { LOGIN_METHOD } from 'src/app/contract-management/models/contract-management.model';
import { ToggleComponent } from "../../../../@core/components/ui/toggle/toggle.component";
import { InputComponent } from 'src/app/@core/components/ui/input/input.component';
import { DialogComponent } from '../../../../@core/components/ui/dialog/dialog.component';
import { UserService } from '../../../services/user.service';
import { UserAvatarPipe } from '../../../../@shared/pipes/user-avatar-pipe';
import { FullNamePipe } from '../../../../@shared/pipes/fullName.pipe';

@Component({
  selector: 'app-organization-setup',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, SelectModule, PhoneInputComponent, CardModule, ButtonModule, DataTableComponent, NgxEchartsModule, TitleCasePipe, ButtonComponent, SelectComponent, ToggleComponent, InputComponent, DialogComponent, UserAvatarPipe, FullNamePipe, IconComponent],
  providers: [
    {
      provide: NGX_ECHARTS_CONFIG,
      useValue: {
        echarts: () => import('echarts')
      }
    }
  ],
  standalone: true,
  templateUrl: './organization-setup.component.html',
  styleUrl: './organization-setup.component.scss'
})
export class OrganizationSetupComponent implements OnInit {

  @ViewChild('logoFileInput') logoFileInput!: ElementRef<HTMLInputElement>;

  loginMethods = [
    { label: 'Standard login with password', value: LOGIN_METHOD.PASSWORD, enabled: false, icon: null },
    { label: 'Google', value: LOGIN_METHOD.GOOGLE, enabled: false, icon: 'icons/google-icon.svg' },
    { label: 'Microsoft', value: LOGIN_METHOD.MICROSOFT, enabled: false, icon: 'icons/microsoft.svg' }
  ];

  USER_ROLE = USER_ROLE;
  ADDITIONAL_ROLES = ADDITIONAL_ROLES;

  organizationForm!: FormGroup;
  workspacePrefix = WORKSPACE_PREFIX;
  workspaceInputValue = '';
  currentUser: IUser | null | undefined
  noOfEmployees: SelectOption[] = NO_OF_EMPLOYEES.map(size => ({ label: size, value: size }));
  companySizeConfig: SelectConfig = {
    disabled: true,
    placeholder: 'Company Size',
    searchable: false,
    clearable: false,
    styleClass: 'border h-10 rounded-lg',
    style: { 'width': '10.5rem' }
  };
  organizationData!: IOrganization
  nonEditable: boolean = true;
  consumptionData: {
    date: string,
    totalTokens: number,
    cost: number
  }[] = []
  consumptionDataLoading: boolean = false
  totalRecords: number = 0
  consumptionColumns: TableColumn[] = []
  consumptionTableConfig: TableConfig = {}
  complianceRate: number = 0
  totalTokens: number = 0
  totalCost: number = 0
  chartOption: echarts.EChartsOption = {}
  BILLING_CYCLE = BILLING_CYCLE

  // Transfer dialog (shared between billing admin and system admin)
  showTransferSystemAdminDialog: boolean = false;
  allUsers: IUser[] = [];
  filteredTransferUsers: IUser[] = [];
  selectedTransferUser: IUser | null = null;
  transferUserSearchQuery: string = '';
  isTransferring: boolean = false;
  isTransferringSystemAdmin: boolean = false;

  // Logo-related properties
  selectedLogoFile: File | null = null;
  currentLogoUrl: string | null = null;
  previewLogoUrl: string | null = null;
  removeLogo: boolean = false;
  validImageTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  isSubmitting: boolean = false;
  originalDomain: string = '';
  logoSizeError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private organizationService: OrganizationSetupFormService,
    private toastService: ToastService,
    private router: Router,
    private consumptionService: ConsumptionService,
    private userService: UserService,
  ) {
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res
    });
  }

  ngOnInit(): void {
    this.initializeConsumptionTable();
    this.initializeForms()
    this.organizationService.fetchOrganizationSetupData().subscribe((res) => {
      this.organizationData = res.data
      this.initializeForms()
      this.initializeLoginMethods()
    })
    this.getConsumptionDetails();
    this.getTodaysConsumption();
    this.chartOption = setGuageChart(this.complianceRate, [GAUGE_COLORS.GREEN, GAUGE_COLORS.YELLOW, GAUGE_COLORS.RED])
  }

  initializeConsumptionTable() {
    this.consumptionColumns = [
      { field: 'rowIndex', header: 'S. No.', type: 'text', width: '80px' },
      { field: 'date', header: 'Date', type: 'text' },
      { field: 'totalTokens', header: 'Token', type: 'text' }
    ];

    this.consumptionTableConfig = {
      paginator: false,
      loading: false,
      emptyMessage: 'No data available',
      rows: 4
    };
  }

  canView() {
    return this.currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role) : false
  }

  canEdit() {
    return this.currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role) : false
  }

  isSystemAdmin(): boolean {
    return this.currentUser?.role === USER_ROLE.SUPER_ADMIN;
  }

  openTransferDialog() {
    this.selectedTransferUser = null;
    this.transferUserSearchQuery = '';
    this.userService.getUsers().subscribe(res => {
      this.allUsers = (res.data?.users ?? []).filter(u => u._id !== this.currentUser?._id);
      this.filteredTransferUsers = [...this.allUsers];
      this.showTransferSystemAdminDialog = true;
    });
  }

  confirmTransferSystemAdmin() {
    if (!this.selectedTransferUser) {
      this.toastService.error('Please select a user to transfer system admin to');
      return;
    }
    this.isTransferringSystemAdmin = true;
    this.userService.transferSystemAdmin(this.selectedTransferUser._id as string).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastService.success('System admin transferred successfully');
          this.showTransferSystemAdminDialog = false;
        } else {
          this.toastService.error(res.message || 'Transfer failed');
        }
        this.isTransferringSystemAdmin = false;
      },
      error: (err: any) => {
        this.toastService.error(err?.error?.message || 'Transfer failed');
        this.isTransferringSystemAdmin = false;
      }
    });
  }

  onTransferUserSearch() {
    const query = this.transferUserSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredTransferUsers = [...this.allUsers];
      return;
    }
    this.filteredTransferUsers = this.allUsers.filter(u => {
      const name = (u.fullName || `${u.firstName} ${u.lastName}`).toLowerCase();
      return name.includes(query) || u.email.toLowerCase().includes(query);
    });
  }

  getTodaysConsumption() {
    this.consumptionService.getTodaysConsumption({ isOrganizationConsumption: true }).subscribe((res) => {
      if (res.success) {
        this.complianceRate = res.data.totalCost
        this.totalTokens = res.data.totalTokens
        this.totalCost = res.data.totalCost
        this.chartOption = setGuageChart(this.complianceRate, [GAUGE_COLORS.GREEN, GAUGE_COLORS.YELLOW, GAUGE_COLORS.RED], 200) // max value is $200 dollars for organization
      }
    })
  }

  getConsumptionDetails() {
    this.consumptionDataLoading = true
    this.consumptionTableConfig = { ...this.consumptionTableConfig, loading: true }
    let filter: {
      organization?: string,
      user?: string[],
      dateRange?: {
        startDate: Date,
        endDate: Date
      },
      skip?: number,
      limit?: number
    } = {}
    filter['organization'] = this.currentUser?.organization as string
    filter['skip'] = 0
    filter['limit'] = 3
    this.consumptionService.getConsumptionDetails(filter).subscribe((res) => {
      if (res.success) {
        this.consumptionData = res.data.data.map((item: any, index: number) => ({
          ...item,
          rowIndex: index + 1
        }));
        this.totalRecords = res.data.totalCount
        this.consumptionDataLoading = false
        this.consumptionTableConfig = { ...this.consumptionTableConfig, loading: false }
      } else {
        this.consumptionDataLoading = false
        this.consumptionTableConfig = { ...this.consumptionTableConfig, loading: false }

      }
    })
  }

  getDateOneYearLater(paidDate: Date) {
    let newDate = new Date(paidDate);
    newDate.setFullYear(newDate.getFullYear() + 1);
    return newDate;
  }

  onCompanyNameChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const companyName = inputElement.value;
    const slugifiedName = slug(companyName, '-').toLocaleLowerCase();
    this.workspaceInputValue = slugifiedName;
    this.organizationForm.get('workspaceUrl')?.setValue(this.workspacePrefix + this.workspaceInputValue);
    this.organizationForm.get('workspaceUrl')?.updateValueAndValidity();
  }

  onUrlInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;

    // Check for existing workspace
    this.authService.checkOrganization(inputValue.split('/')[1]).subscribe((res: { success: boolean }) => {
      if (res.success) {
        this.organizationForm.controls['workspaceUrl'].setErrors({ 'alreadyExists': true });
      }
    });

    if (inputValue.startsWith(this.workspacePrefix)) {
      this.workspaceInputValue = inputValue.replace(this.workspacePrefix, '');
      this.workspaceInputValue = this.workspaceInputValue.replace(/[^a-zA-Z0-9-]/g, '-');
      this.workspaceInputValue = slug(this.workspaceInputValue, '-').toLocaleLowerCase();
    } else {
      inputElement.value = this.workspacePrefix + this.workspaceInputValue;
    }
  }

  parseContactNumber(contactData: any): any {
    if (!contactData) return '';
    if (typeof contactData === 'object' && contactData !== null) {
      return contactData;
    }
    if (typeof contactData === 'string') {
      if (contactData.trim().startsWith('{') && contactData.trim().endsWith('}')) {
        return JSON.parse(contactData);
      }
      return contactData;
    }
    return contactData;
  }

  initializeForms(): void {
    this.currentLogoUrl = this.organizationData?.organizationLogoUrl
      ? this.getFullLogoUrl(this.organizationData.organizationLogoUrl)
      : null;

    this.previewLogoUrl = this.currentLogoUrl;

    this.originalDomain = this.organizationData?.domain || '';

    this.organizationForm = this.fb.group({
      companyName: [this.organizationData?.name || '', Validators.required],
      workspaceUrl: [
        this.organizationData?.workspaceSlug
          ? this.organizationData.workspaceSlug
          : this.workspacePrefix + ''
      ],
      companySize: [this.organizationData?.size || ''],
      contact: [this.parseContactNumber(this.organizationData?.company_contact_no)],
      address: [this.organizationData?.office_address || ''],
      email: [this.organizationData?.email || '', [Validators.required, Validators.email]],
      domain: [this.organizationData?.domain || '', [Validators.required]]
    });

    this.organizationForm.disable();
  }

  initializeLoginMethods(): void {
    if (this.organizationData?.loginMethods && this.organizationData.loginMethods.length > 0) {
      // Update loginMethods based on organization data
      this.loginMethods.forEach(method => {
        method.enabled = this.organizationData.loginMethods!.includes(method.value);
      });
    }
  }

  private getFullLogoUrl(logoUrl: string): string {
    if (!logoUrl) return '';

    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }

    const baseUrl = environment.s3Url.endsWith('/') ? environment.s3Url.slice(0, -1) : environment.s3Url;
    const path = logoUrl.startsWith('/') ? logoUrl : '/' + logoUrl;
    return baseUrl + path;
  }

  enterEditMode() {
    if (this.nonEditable) {
      this.nonEditable = false;
      this.organizationForm.enable();
      this.removeLogo = false;
      this.selectedLogoFile = null;
      this.previewLogoUrl = this.currentLogoUrl;
      this.logoSizeError = null;
      this.companySizeConfig = { ...this.companySizeConfig, disabled: false };
    } else {
      this.cancelEdit();
    }
  }

  triggerLogoFileInput() {
    if (!this.nonEditable) {
      this.logoFileInput.nativeElement.click();
    }
  }

  onLogoSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file && this.isValidImageType(file.type)) {
      const maxSize = 1000 * 1000; // 1MB
      if (file.size > maxSize) {
        this.logoSizeError = 'Image should be <1MB';
        // Revert to original logo
        if (this.previewLogoUrl && this.previewLogoUrl !== this.currentLogoUrl) {
          URL.revokeObjectURL(this.previewLogoUrl);
        }
        this.previewLogoUrl = this.currentLogoUrl;
        this.selectedLogoFile = null;
        input.value = '';
        return;
      }

      this.logoSizeError = null;
      this.selectedLogoFile = file;
      this.removeLogo = false;
      if (this.previewLogoUrl && this.previewLogoUrl !== this.currentLogoUrl) {
        URL.revokeObjectURL(this.previewLogoUrl);
      }
      this.previewLogoUrl = URL.createObjectURL(file);
    } else {
      this.toastService.error('Please select a valid image file (JPEG, PNG, GIF)');
    }
    input.value = '';
  }

  isValidImageType(type: string): boolean {
    return this.validImageTypes.includes(type);
  }

  removeSelectedLogo() {
    if (this.previewLogoUrl && this.previewLogoUrl !== this.currentLogoUrl) {
      URL.revokeObjectURL(this.previewLogoUrl);
    }

    this.selectedLogoFile = null;
    this.removeLogo = true;
    this.previewLogoUrl = null;
    this.logoSizeError = null;
  }

  validateForm() {
    if (!this.organizationForm.valid) {
      this.organizationForm.markAllAsTouched();
      return;
    }

    const currentDomain = this.organizationForm.get('domain')?.value;
    if (currentDomain !== this.originalDomain) {
      this.authService.checkDomain(currentDomain).subscribe(res => {
        if (res.data) {
          this.organizationForm.controls['domain'].setErrors({ 'alreadyExists': true });
          this.toastService.error('Domain already exists');
          return;
        }
        this.onSubmitOrganization();
      });
      return;
    }

    this.onSubmitOrganization();
  }

  onSubmitOrganization() {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const formData = new FormData();

    const formValue = this.organizationForm.value;
    formData.append('companyName', formValue.companyName || '');
    formData.append('workspaceUrl', formValue.workspaceUrl || '');
    formData.append('companySize', formValue.companySize || '');
    formData.append('address', formValue.address || '');
    formData.append('email', formValue.email || '');
    formData.append('domain', formValue.domain || '');

    if (formValue.contact) {
      if (typeof formValue.contact === 'string') {
        formData.append('contact', formValue.contact);
      } else {
        formData.append('contact', JSON.stringify(formValue.contact));
      }
    }

    if (this.selectedLogoFile) {
      formData.append('companyLogo', this.selectedLogoFile);
    } else if (this.removeLogo) {
      formData.append('removeLogo', 'true');
    }

    this.organizationService.updateOrganizationSetupData(formData).subscribe(() => {
      this.toastService.success("Organization details updated successfully");
      this.nonEditable = true;
      this.organizationForm.disable();
      this.isSubmitting = false;
      this.companySizeConfig = { ...this.companySizeConfig, disabled: true };
      this.selectedLogoFile = null;
      this.removeLogo = false;
      this.logoSizeError = null;

      if (this.previewLogoUrl && this.previewLogoUrl !== this.currentLogoUrl) {
        URL.revokeObjectURL(this.previewLogoUrl);
      }

      this.organizationService.fetchOrganizationSetupData().subscribe((res) => {
        this.organizationData = res.data;
        this.initializeForms();
      });
    });
  }

  cancelEdit() {
    this.nonEditable = true;
    this.companySizeConfig = { ...this.companySizeConfig, disabled: true };
    this.selectedLogoFile = null;
    this.removeLogo = false;
    this.logoSizeError = null;
    if (this.previewLogoUrl && this.previewLogoUrl !== this.currentLogoUrl) {
      URL.revokeObjectURL(this.previewLogoUrl);
    }
    this.previewLogoUrl = this.currentLogoUrl;
    this.initializeForms();
  }

  navigateToConsumptionPage() {
    this.router.navigate(['/consumption'], { queryParams: { organization: this.currentUser?.organization } });
  }

  navigateToPricing() {
    this.router.navigate(['/pricing']);
  }

  AddNewLogo() {
    this.triggerLogoFileInput();
  }

  get displayLogoUrl(): string | null {
    return this.previewLogoUrl || this.currentLogoUrl;
  }

  onLoginMethodToggle(method: any, enabled: boolean) {
    // Check if user is trying to disable a method
    if (!enabled) {
      // Count how many methods are currently enabled (excluding the current one being toggled)
      const enabledCount = this.loginMethods.filter(m => m.value !== method.value && m.enabled).length;

      // If no other methods are enabled, prevent disabling this one
      if (enabledCount === 0) {
        this.toastService.error('At least one login method must be enabled');
        // Revert the toggle back to enabled using setTimeout to ensure change detection
        setTimeout(() => {
          method.enabled = true;
        }, 0);
        return;
      }
    }

    // Get all enabled login methods
    const enabledLoginMethods = this.loginMethods
      .filter(m => m.enabled)
      .map(m => m.value);

    // Call API to save login methods
    this.organizationService.saveLoginMethods({ loginMethods: enabledLoginMethods }).subscribe((res) => {
      if (res.success) {
        this.toastService.success(`${method.label} ${enabled ? 'enabled' : 'disabled'} successfully`);
      } else {
        this.toastService.error('Failed to update login method');
        // Revert the toggle on error
        setTimeout(() => {
          method.enabled = !enabled;
        }, 0);
      }
    });
  }

  ngOnDestroy() {
    if (this.previewLogoUrl && this.previewLogoUrl !== this.currentLogoUrl) {
      URL.revokeObjectURL(this.previewLogoUrl);
    }
  }
}
